import PropTypes from 'prop-types';
import React from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

import escapeHtml from 'scripts/lib/escape_html';
import OpenAttachmentPreview from 'actions/modal/open_attachment_preview';
import { useExecuteAction } from 'components/hooks/connect_hooks';
import DownloadableAttachment from 'models/downloadable_attachment';

/**
 * What `analyze_image` found in the customer's files, from glados's
 * `image_analysis` card: one table per file, with the rows V1's prompt had the
 * model write, in V1's order, each only when its fields are present. Every value
 * is customer- or model-supplied, so it renders as text, never as HTML.
 *
 * The recommendation is not here: it's the model's interpretation, written in
 * the answer after the card.
 */
export default function TeamAssistV2ImageAnalysisCard({ result }) {
  const { t } = useTranslation();
  const executeAction = useExecuteAction();
  const { results, failedCount } = readImageAnalysis(result);

  if (results.length === 0 && failedCount === 0) {
    return null;
  }

  // Every file the card can open, so the preview can page between them.
  const previewable = results.filter(isOpenable);
  const openFile = fileResult =>
    executeAction(OpenAttachmentPreview, {
      attachments: previewable.map(toDownloadableAttachment),
      currentIndex: previewable.indexOf(fileResult),
    });

  return (
    <div data-aid="teamAssistV2ImageAnalysisCard">
      {results.map((fileResult, i) => (
        <ImageAnalysisTable
          fileResult={fileResult}
          key={fileResult.attachmentId || i}
          onOpen={isOpenable(fileResult) ? () => openFile(fileResult) : undefined}
        />
      ))}
      {failedCount > 0 && (
        <FailedNote data-aid="teamAssistV2ImageAnalysisFailed">
          {t("Couldn't analyze {{count}} file", { count: failedCount })}
        </FailedNote>
      )}
    </div>
  );
}

TeamAssistV2ImageAnalysisCard.propTypes = {
  result: PropTypes.shape({
    failedCount: PropTypes.number,
    results: PropTypes.arrayOf(PropTypes.object),
  }),
};

function ImageAnalysisTable({ fileResult, onOpen }) {
  const { i18n, t } = useTranslation();
  const rows = imageAnalysisRows(fileResult, { locale: i18n.resolvedLanguage || i18n.language, t });

  return (
    <table className="teamAssistImageVerificationTable" data-aid="teamAssistV2ImageAnalysisTable">
      <tbody>
        {rows.map(row => (
          <tr key={row.label}>
            <th scope="row">{row.label}</th>
            <td>
              {row.isFileName && onOpen ? (
                <FileButton
                  aria-label={t('Open {{filename}}', { filename: row.lines[0] })}
                  data-aid="teamAssistV2ImageAnalysisOpenFile"
                  onClick={onOpen}
                  type="button"
                >
                  {row.lines[0]}
                </FileButton>
              ) : (
                row.lines.map((line, i) => <div key={i}>{line}</div>)
              )}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

ImageAnalysisTable.propTypes = {
  fileResult: PropTypes.object.isRequired,
  onOpen: PropTypes.func,
};

/**
 * The card as HTML for Copy: the same tables with the same rows, so a copied
 * answer keeps the facts, as V1's did. Every value is escaped.
 */
export function imageAnalysisHtml(result, { locale, t }) {
  const { results, failedCount } = readImageAnalysis(result);
  const parts = results.map(fileResult => {
    const rows = imageAnalysisRows(fileResult, { locale, t }).map(
      row => `<tr><th>${escapeHtml(row.label)}</th><td>${row.lines.map(escapeHtml).join('<br />')}</td></tr>`
    );
    return `<table><tbody>${rows.join('')}</tbody></table>`;
  });
  if (failedCount > 0) {
    parts.push(`<div>${escapeHtml(t("Couldn't analyze {{count}} file", { count: failedCount }))}</div>`);
  }
  return parts.join('');
}

function readImageAnalysis(result) {
  return {
    results: Array.isArray(result?.results) ? result.results : [],
    failedCount: Number.isInteger(result?.failedCount) ? result.failedCount : 0,
  };
}

/**
 * A file the card can open: one glados recorded the conversation item for.
 * Cards saved before that show the name as plain text.
 */
function isOpenable(fileResult) {
  return Boolean(fileResult.conversationItemId && fileResult.attachmentId);
}

/**
 * The file as the attachment preview takes it. glados's by-id redirect finds
 * any channel's file from its conversation item and attachment id.
 */
function toDownloadableAttachment(fileResult) {
  const { attachmentId, conversationItemId, contentType, filename } = fileResult;
  return DownloadableAttachment.create({
    id: attachmentId,
    downloadUrl: `/sidekick/api/communication-sessions/items/${encodeURIComponent(
      conversationItemId
    )}/attachments/${encodeURIComponent(attachmentId)}`,
    fileDescriptor: {
      contentType: contentType || contentTypeFromName(filename),
      filename: filename || attachmentId,
    },
  });
}

// For cards saved before glados recorded the type.
function contentTypeFromName(filename) {
  const extension = String(filename || '')
    .split('.')
    .pop()
    .toLowerCase();
  return CONTENT_TYPES_BY_EXTENSION[extension] || '';
}

const CONTENT_TYPES_BY_EXTENSION = {
  gif: 'image/gif',
  jpeg: 'image/jpeg',
  jpg: 'image/jpeg',
  pdf: 'application/pdf',
  png: 'image/png',
  webp: 'image/webp',
};

/**
 * The table's rows for one file, as `{ label, lines }`, in V1's order and with
 * V1's rules for when each appears.
 */
function imageAnalysisRows(fileResult, { locale, t }) {
  const nameRow = { label: t('Image'), lines: [imageName(fileResult)], isFileName: true };
  // A refused file's quality and content safety are placeholders, not readings.
  if (fileResult.providerBlocked) {
    return [
      nameRow,
      { label: t('Status'), lines: [t("We couldn't analyze this image. It may contain unsafe content.")] },
    ];
  }
  const rows = [
    nameRow,
    { label: t('Capture'), lines: captureLines(fileResult.exif, { locale, t }) },
    { label: t('Product'), lines: productLines(fileResult.product) },
    { label: t('Damage'), lines: damageLines(fileResult.damage, t) },
    { label: t('Claim match'), lines: claimMatchLines(fileResult.claimMatch, t) },
    { label: t('Authenticity'), lines: authenticityLines(fileResult.authenticity, t) },
    { label: t('Quality'), lines: qualityLines(fileResult.imageQuality, t) },
    { label: t('Extracted'), lines: extractedLines(fileResult.custom) },
    { label: t('Observed'), lines: observedLines(fileResult) },
  ];
  return rows.filter(row => row.lines.length > 0);
}

function imageName({ attachmentId, filename }) {
  if (filename) {
    return filename;
  }
  return `…${String(attachmentId || '').slice(-ATTACHMENT_ID_SUFFIX_LENGTH)}`;
}

function captureLines(exif, { locale, t }) {
  if (!exif) {
    return [];
  }
  const lines = [];
  if (exif.capturedAt) {
    lines.push(formatCapturedAt(exif.capturedAt, locale));
  }
  // glados keeps whether a photo recorded a location, never where.
  if (exif.hasLocation) {
    lines.push(t('Location recorded'));
  }
  return lines;
}

function productLines(product) {
  if (!product) {
    return [];
  }
  if (product.productName) {
    return [product.brand ? `${product.productName} (${product.brand})` : product.productName];
  }
  return product.description ? [product.description] : [];
}

function damageLines(damage, t) {
  if (!damage) {
    return [];
  }
  return [`${severityLabel(damage.severity, t)} — ${damage.description || ''}`.trim()];
}

function severityLabel(severity, t) {
  switch (severity) {
    case 'minor':
      return t('Minor');
    case 'moderate':
      return t('Moderate');
    case 'severe':
      return t('Severe');
    default:
      return String(severity || '');
  }
}

function claimMatchLines(claimMatch, t) {
  if (!claimMatch) {
    return [];
  }
  const confidence = Math.round(claimMatch.confidence);
  return [
    claimMatch.supported
      ? t('Supported ({{confidence}}%)', { confidence })
      : t('Not supported ({{confidence}}%)', { confidence }),
  ];
}

/**
 * The stock score as a fact. Only a score above 70 is called likely stock;
 * reading anything lower is the model's job, in its answer.
 */
function authenticityLines(authenticity, t) {
  if (!authenticity) {
    return [];
  }
  const score = Math.round(authenticity.stockScore);
  const lines = [
    score > LIKELY_STOCK_ABOVE
      ? t('Likely a stock photo (stock score {{score}} of 100)', { score })
      : t('Stock score {{score}} of 100', { score }),
  ];
  if (authenticity.watermarked) {
    lines.push(t('Watermarked'));
  }
  return lines;
}

function qualityLines(imageQuality, t) {
  if (!imageQuality || !Number.isFinite(imageQuality.clarity)) {
    return [];
  }
  return [t('{{clarity}} out of 100', { clarity: Math.round(imageQuality.clarity) })];
}

function extractedLines(custom) {
  if (!custom || typeof custom !== 'object') {
    return [];
  }
  return Object.entries(custom).map(([name, value]) => `${name}: ${customValueText(value)}`);
}

function customValueText(value) {
  if (value === null || value === undefined) {
    return '';
  }
  return typeof value === 'object' ? JSON.stringify(value) : String(value);
}

// V1: the claim match's evidence, or the damage description when there's no
// claim match.
function observedLines({ claimMatch, damage }) {
  const observed = (claimMatch ? claimMatch.evidence : damage?.description) || '';
  if (!observed) {
    return [];
  }
  return [observed.length > OBSERVED_MAX_LENGTH ? `${observed.slice(0, OBSERVED_MAX_LENGTH).trimEnd()}…` : observed];
}

/**
 * A capture time as the camera's clock read it, with the offset glados adds
 * when the camera recorded one, never moved into the agent's time zone: the
 * point is when the photo was taken where it was taken. Anything unparseable
 * is shown raw.
 */
function formatCapturedAt(capturedAt, locale) {
  const match = CAPTURED_AT.exec(String(capturedAt));
  if (!match) {
    return String(capturedAt);
  }
  const [, year, month, day, hour, minute, second, offset] = match;
  const wallClock = new Date(Date.UTC(+year, +month - 1, +day, +hour, +minute, +(second || 0)));
  try {
    const formatted = new Intl.DateTimeFormat(locale, {
      dateStyle: 'medium',
      timeStyle: 'short',
      timeZone: 'UTC',
    }).format(wallClock);
    return offset ? `${formatted} (UTC${offset === 'Z' ? '' : offset})` : formatted;
  } catch {
    return String(capturedAt);
  }
}

const ATTACHMENT_ID_SUFFIX_LENGTH = 6;
// `YYYY-MM-DDTHH:mm[:ss]`, then `Z` or `±HH:MM` when the camera wrote an offset.
const CAPTURED_AT = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2})(?:\.\d+)?)?(Z|[+-]\d{2}:\d{2})?$/;
const LIKELY_STOCK_ABOVE = 70;
const OBSERVED_MAX_LENGTH = 200;

const FileButton = styled.button`
  background: none;
  border: none;
  color: ${p => p.theme.colors.green400};
  cursor: pointer;
  font: inherit;
  padding: 0;
  text-align: left;
  text-decoration: underline;
`;

const FailedNote = styled.div`
  color: ${p => p.theme.colors.gray800};
  font-style: italic;
  margin: 4px 0 8px;
`;
